import { useEffect, useState } from "react";
import { Trash2, MailOpen, FileDown, Eye, Handshake, Building2, Briefcase, MapPin, Clock, IndianRupee, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Pager from "@/components/site/Pager";
import EnquiryDetailDialog from "@/components/admin/EnquiryDetailDialog";
import { api, API_BASE, getToken } from "@/lib/api";
import { toast } from "sonner";

const PAGE_SIZE = 20;

const URGENCIES = ["", "Immediate (within 2 weeks)", "Within a month", "Within a quarter", "Exploratory"];

export default function AdminHireBriefs() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [urgency, setUrgency] = useState("");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/admin/enquiries", { params: { type: "hire_talent", urgency: urgency || undefined, q: q || undefined, page, page_size: PAGE_SIZE } })
    .then((r) => {
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, urgency, q]);
  useEffect(() => { setPage(1); }, [urgency, q]);

  const toggleRead = async (id, val) => { await api.patch(`/admin/enquiries/${id}/read?read=${val}`); load(); };
  const remove = async (id) => {
    if (!window.confirm("Delete this brief?")) return;
    await api.delete(`/admin/enquiries/${id}`);
    toast.success("Deleted"); setDetail(null); load();
  };

  const openDetail = async (row) => {
    setDetail(row);
    if (!row.is_read) {
      await api.patch(`/admin/enquiries/${row.id}/read?read=true`);
      load();
    }
  };

  const exportCsv = async () => {
    const token = getToken();
    const res = await fetch(`${API_BASE}/admin/exports/enquiries?type=hire_talent`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Export failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = `hire-briefs-${new Date().toISOString().slice(0,10)}.csv`; link.click();
    URL.revokeObjectURL(url);
    toast.success("Exported");
  };

  const unread = items.filter((x) => !x.is_read).length;

  return (
    <div className="space-y-6" data-testid="admin-hire-briefs">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-semibold text-slate-900">
            <Handshake className="h-6 w-6 text-secondary" /> Hire Briefs
          </h1>
          <p className="text-sm text-slate-500">Employer requirements submitted via the Hire Talent form · {total} total{unread ? ` · ${unread} unread on this page` : ""}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search company / role / location…"
            className="md:w-64"
            data-testid="hire-briefs-search"
          />
          <Select value={urgency || "all"} onValueChange={(v) => setUrgency(v === "all" ? "" : v)}>
            <SelectTrigger className="md:w-52" data-testid="hire-briefs-urgency-filter"><SelectValue placeholder="All urgencies" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All urgencies</SelectItem>
              {URGENCIES.filter(Boolean).map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={exportCsv} data-testid="hire-briefs-export-csv"><FileDown className="mr-1 h-4 w-4" />CSV</Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {items.map((e) => (
          <div key={e.id} className={`group rounded-2xl border p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${e.is_read ? "border-slate-200 bg-white" : "border-secondary/30 bg-secondary/5"}`} data-testid={`hire-brief-card-${e.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 shrink-0 text-primary" />
                  <div className="truncate font-heading text-base font-semibold text-slate-900">{e.company || "—"}</div>
                </div>
                <div className="mt-0.5 truncate text-xs text-slate-500">{e.name || "—"}{e.designation ? ` · ${e.designation}` : ""}</div>
              </div>
              {!e.is_read && <Badge className="bg-secondary text-secondary-foreground">New</Badge>}
            </div>

            <div className="mt-3 space-y-1.5 text-sm text-slate-700">
              {e.role_needed && <Row icon={Briefcase} value={<b className="truncate">{e.role_needed}</b>} extra={e.headcount ? `× ${e.headcount}` : ""} />}
              {e.department && <Row icon={Users} value={e.department} muted />}
              {e.job_location && <Row icon={MapPin} value={e.job_location} muted />}
              {e.salary_range && <Row icon={IndianRupee} value={e.salary_range} muted />}
              {e.experience_required && <Row icon={Clock} value={e.experience_required} muted />}
              {e.urgency && <Row icon={Clock} value={<span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">{e.urgency}</span>} />}
            </div>

            <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3 text-xs text-slate-500">
              <span>{new Date(e.created_at).toLocaleDateString()}</span>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => openDetail(e)} data-testid={`hire-brief-view-${e.id}`}>
                  <Eye className="h-3.5 w-3.5" /> <span className="ml-1">View</span>
                </Button>
                <Button variant="ghost" size="sm" onClick={() => toggleRead(e.id, !e.is_read)} data-testid={`hire-brief-read-${e.id}`} title={e.is_read ? "Mark unread" : "Mark read"}>
                  <MailOpen className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => remove(e.id)} data-testid={`hire-brief-delete-${e.id}`}>
                  <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                </Button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="col-span-full rounded-xl border border-dashed border-slate-300 p-12 text-center text-slate-400">
            No hire briefs yet.
          </div>
        )}
      </div>

      <Pager page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} testIdPrefix="admin-hire-briefs-pager" />

      <EnquiryDetailDialog
        enquiry={detail}
        open={!!detail}
        onClose={() => setDetail(null)}
        onToggleRead={(row) => { toggleRead(row.id, !row.is_read); setDetail({ ...row, is_read: !row.is_read }); }}
        onDelete={(row) => remove(row.id)}
      />
    </div>
  );
}

function Row({ icon: Icon, value, extra, muted }) {
  return (
    <div className={`flex items-center gap-2 ${muted ? "text-slate-600" : "text-slate-800"}`}>
      <Icon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
      <span className="min-w-0 truncate">{value}</span>
      {extra && <span className="shrink-0 text-xs text-slate-400">{extra}</span>}
    </div>
  );
}
